import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Shield, Lock, FileText, Database, Eye, AlertTriangle } from "lucide-react";

export default function CompliancePage() {
  usePageTitle("Compliance", "HIPAA compliance documentation, data usage policies, and chain-of-custody agreements for HealthWallet.");

  return (
    <div className="min-h-screen" data-testid="page-compliance">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6">
        <Link href="/">
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground mb-6 cursor-pointer" data-testid="link-back-home">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </span>
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" data-testid="text-compliance-title">
          Compliance & Security
        </h1>
        <p className="text-muted-foreground mb-8">Last updated: April 18, 2026</p>

        <div className="space-y-6">

          <Card data-testid="section-hipaa">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                HIPAA Compliance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                HealthWallet operates as a healthcare-adjacent platform and adheres to the Health Insurance Portability and Accountability Act (HIPAA) requirements applicable to our role in the healthcare ecosystem. We act as a Business Associate where required and maintain appropriate safeguards for Protected Health Information (PHI).
              </p>
              <p>
                Our HIPAA compliance program includes administrative, physical, and technical safeguards designed to protect the confidentiality, integrity, and availability of any PHI we handle on behalf of our covered entity partners.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Annual risk assessments and workforce training</li>
                <li>Business Associate Agreements (BAAs) executed with all covered entity partners</li>
                <li>Minimum necessary data access policies enforced at the application and database layers</li>
                <li>Breach notification procedures compliant with the HIPAA Breach Notification Rule</li>
                <li>Designated Privacy Officer and Security Officer roles</li>
              </ul>
              <p>
                To request a copy of our HIPAA compliance documentation or to execute a BAA, contact{" "}
                <a href="mailto:compliance@healthwallet.ai" className="text-primary underline underline-offset-4">
                  compliance@healthwallet.ai
                </a>.
              </p>
            </CardContent>
          </Card>

          <Card data-testid="section-data-usage">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                Data Usage Policy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                HealthWallet collects only the data necessary to provide our community healthcare funding services. We never sell personal or health-related data to third parties.
              </p>
              <p><strong className="text-foreground">Data we collect:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Account registration information (name, email, phone)</li>
                <li>Wallet funding and transaction records</li>
                <li>Health condition categories selected by users for fund enrollment (no diagnosis codes or clinical records)</li>
                <li>Contribution and payment histories</li>
                <li>Identity verification data where required by financial regulations</li>
              </ul>
              <p><strong className="text-foreground">How we use your data:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>To operate your HealthWallet account and process transactions</li>
                <li>To match community contributions to eligible patient wallets</li>
                <li>To comply with applicable financial, tax, and healthcare regulations</li>
                <li>To generate aggregate, de-identified analytics that improve the platform</li>
              </ul>
              <p>
                Data retention periods vary by data type. Financial transaction records are retained for a minimum of seven years in compliance with IRS and state financial regulations. Account data is deleted within 30 days of account closure upon verified request.
              </p>
            </CardContent>
          </Card>

          <Card data-testid="section-chain-of-custody">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Chain of Custody
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                HealthWallet maintains a complete, auditable chain of custody for all funds that flow through the platform. Every contribution, allocation, disbursement, and refund is logged with an immutable timestamp, actor identity, and transaction ID.
              </p>
              <p><strong className="text-foreground">Fund traceability guarantees:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Every dollar contributed is traceable to a specific community fund or patient wallet</li>
                <li>All disbursements to healthcare providers are recorded against a specific wallet and authorization event</li>
                <li>Dispute resolution audit logs are retained for a minimum of seven years</li>
                <li>Employer and supporter contributions are individually reconcilable for tax documentation purposes</li>
              </ul>
              <p>
                Healthcare provider partners can request a transaction audit report for any payment received via HealthWallet. Employer plan administrators have access to aggregate contribution reports through the employer dashboard.
              </p>
            </CardContent>
          </Card>

          <Card data-testid="section-security">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                Security Standards
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                HealthWallet employs industry-standard security controls appropriate to a financial and healthcare-adjacent platform.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>TLS 1.2+ encryption for all data in transit</li>
                <li>AES-256 encryption for sensitive data at rest</li>
                <li>Payment processing through Stripe (PCI DSS Level 1 certified) — HealthWallet never stores raw card numbers</li>
                <li>Role-based access controls with principle of least privilege</li>
                <li>Multi-factor authentication available for all account types</li>
                <li>Regular penetration testing and vulnerability scanning</li>
                <li>SOC 2 Type II audit in progress for 2026 certification</li>
              </ul>
            </CardContent>
          </Card>

          <Card data-testid="section-access-rights">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                Your Rights & Data Access
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                You have the right to access, correct, export, or delete your personal data at any time, subject to applicable legal retention requirements.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">Access:</strong> View and download your account data from the Profile &amp; Settings pages</li>
                <li><strong className="text-foreground">Correction:</strong> Update your information directly through your account settings</li>
                <li><strong className="text-foreground">Deletion:</strong> Request account deletion by contacting support — subject to financial record retention obligations</li>
                <li><strong className="text-foreground">Portability:</strong> Request a machine-readable export of your transaction history</li>
              </ul>
              <p>
                To exercise these rights, contact{" "}
                <a href="mailto:privacy@healthwallet.ai" className="text-primary underline underline-offset-4">
                  privacy@healthwallet.ai
                </a>.
                Minnesota residents have additional rights under the Minnesota Consumer Data Privacy Act (MCDPA).
              </p>
            </CardContent>
          </Card>

          <Card data-testid="section-reporting">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-primary" />
                Reporting a Concern
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                To report a potential security vulnerability, privacy concern, or compliance issue, contact our security team at{" "}
                <a href="mailto:security@healthwallet.ai" className="text-primary underline underline-offset-4">
                  security@healthwallet.ai
                </a>.
                We operate a responsible disclosure program and will acknowledge reports within two business days.
              </p>
              <p>
                For HIPAA-related complaints, you also have the right to file a complaint directly with the U.S. Department of Health and Human Services Office for Civil Rights at{" "}
                <a href="https://www.hhs.gov/hipaa/filing-a-complaint" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">
                  hhs.gov/hipaa/filing-a-complaint
                </a>.
              </p>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}
